/* eslint-disable no-undef */
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';
import { faPencilAlt } from '@fortawesome/pro-regular-svg-icons/faPencilAlt';
import { faPrint } from '@fortawesome/pro-regular-svg-icons/faPrint';
import { faShareAlt } from '@fortawesome/pro-regular-svg-icons/faShareAlt';
import { Note } from '@propertypal/shared/src/types/notes';
import { Property } from '@propertypal/shared/src/types/property';
import Link from 'next/link';
import router from 'next/router';
import React, { FunctionComponent, useMemo } from 'react';
import { useTheme } from 'styled-components';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import OpenViewingOverlay from '../property-box/OpenViewingOverlay';
import PropertyBadge from '../property-box/PropertyBadge';
import PropertyFavourite from '../property-box/PropertyFavourite';
import { FavouriteBox, Item } from '../property-box/PropertyFavourite.style';
import ShowHomesBox from '../property-box/ShowHomesBox';
import { Text, TextLink } from '../typography';
import PropertyPrice from './PropertyPrice';
import {
  Container,
  SummaryBox,
  TopBox,
  ActionsContainer,
  IconLink,
  IconContainer,
  AddressHeader,
  SummaryText,
} from './PropertySummary.style.pn';

interface Props {
  property: Property;
  favourited: boolean;
  toggleFavourited: (favourited: boolean) => Promise<boolean>;
  onShare: () => void;
  loggedIn: boolean;
  hasFeaturedBrochure: boolean;
  showNotesModal: boolean;
  notes: Note[];
}

const PropertySummaryPn: FunctionComponent<Props> = (props) => {
  const theme = useTheme();

  const toggleNotes = () => {
    if (!props.showNotesModal) {
      if (!props.loggedIn) {
        router.push(`/login?sourcePath=${window.location.pathname}/notes`);
      } else {
        router.replace(`${props.property.path}/notes`, undefined, { shallow: true });
      }
    }
  };

  const address = useMemo(() => {
    const addr: string[] = [];
    if (props.property.displayAddressLine1) addr.push(props.property.displayAddressLine1);
    if (props.property.displayAddressLine2) addr.push(props.property.displayAddressLine2);

    return addr?.join(', ');
  }, [props.property.displayAddressLine1, props.property.displayAddressLine2]);

  return (
    <Container>
      <SummaryBox>
        <TopBox>
          <PropertyBadge property={props.property} showStatusBadge disablePlaceholder />

          <ActionsContainer>
            {props.property.type === 'property' && (
              <IconLink
                href={`/property-print?property=${props.property.id}`}
                tabIndex={-1}
                className="hide-print pp-favourite-wrapper"
                data-testid="property-print-button"
              >
                <FavouriteBox playState="paused">
                  <Item opacity={1} size={18}>
                    <FontAwesomeIcon icon={faPrint} size={18} />
                  </Item>
                </FavouriteBox>

                <span>Printer Friendly</span>
              </IconLink>
            )}
            {props.hasFeaturedBrochure && (
              <IconLink
                href={`${props.property.path}/brochure/1`}
                tabIndex={-1}
                className="hide-print pp-favourite-wrapper"
                data-testid="property-brocure-button"
              >
                <FavouriteBox playState="paused">
                  <Item opacity={1} size={18}>
                    <FontAwesomeIcon icon={faFilePdf} size={18} />
                  </Item>
                </FavouriteBox>

                <span>Brochure</span>
              </IconLink>
            )}

            {props.property.published && (
              <IconContainer
                type="button"
                onClick={props.onShare}
                tabIndex={-1}
                className="hide-print pp-favourite-wrapper"
                data-testid="property-share-button"
              >
                <FavouriteBox playState="paused">
                  <Item opacity={1} size={18}>
                    <FontAwesomeIcon icon={faShareAlt} size={18} />
                  </Item>
                </FavouriteBox>

                <span>Share</span>
              </IconContainer>
            )}

            {props.property.published && props.property.type === 'property' && (
              <IconContainer
                type="button"
                onClick={toggleNotes}
                tabIndex={-1}
                className="hide-print"
                data-testid="property-note-button"
              >
                <FavouriteBox playState="paused">
                  <Item opacity={1} size={18}>
                    <FontAwesomeIcon icon={faPencilAlt} size={18} />
                  </Item>
                </FavouriteBox>

                <span>{props.notes?.length ? 'Edit' : 'Add'} Note</span>
              </IconContainer>
            )}

            {props.property.type === 'property' && (
              <PropertyFavourite
                favourited={props.favourited}
                onFavourite={props.toggleFavourited}
                showLabel
                size={18}
              />
            )}
          </ActionsContainer>
        </TopBox>

        <AddressHeader fontWeight="400" data-testid="address-line-1">
          {address}
        </AddressHeader>

        {props.property.briefText && (
          <Text fontSize={18} data-testid="property-brief-text">
            {props.property.briefText}
          </Text>
        )}

        {props.property.summary && (
          <SummaryText
            html={props.property.summary}
            testId="property-summary-html"
            $hasBriefText={!!props.property.briefText}
          />
        )}

        {props.property.development && (
          <Text fontSize={16} mt={15} color={theme.primary}>
            <Link
              href={props.property.development.path}
              style={{ textDecoration: 'underline' }}
              className="pp-primary-text"
            >
              This property forms part of the <b>{props.property.development.name}</b> development <Chevron size={9} />
            </Link>
          </Text>
        )}

        <PropertyPrice
          fontSize={50}
          altFontSize={17}
          price={props.property.price}
          alternativeCurrencyPrice={props.property.alternativeCurrencyPrice}
          containerStyle={{ marginTop: 15 }}
          status={props.property.status}
          comingSoonText={props.property.comingSoonText}
          prefixBreak
          primaryColor={theme.textDark}
        />

        {props.property.includeFeeCharges && (
          <p style={{ marginTop: 10 }}>
            <TextLink
              href={`/letting-agent-fees/${props.property.account.accountNumber}`}
              prefetch={false}
              data-testid="fees-link"
              fontSize={14}
            >
              {' '}
              (some fees may apply)
            </TextLink>
          </p>
        )}

        {props.property.openViewing && (
          <OpenViewingOverlay
            containerStyle={{ marginRight: 10, marginBottom: 15 }}
            openViewing={props.property.openViewing}
            themeName="light"
          />
        )}

        <ShowHomesBox property={props.property} containerStyle={{ marginTop: 30 }} />
      </SummaryBox>
    </Container>
  );
};

export default PropertySummaryPn;
